import React, { useState } from 'react';
import {
  Box, Container, Grid, Card, CardContent, Typography, TextField,
  Button, CircularProgress, Divider,
} from '@mui/material';
import {
  LocationOn, Phone, Email, AccessTime, Send, CheckCircle, LocalHospital,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { emergencyNumbers, hospitalInfo } from '../data/hospitalData';
import PageHero from '../components/common/PageHero';

interface ContactForm { name: string; email: string; subject: string; message: string; }
interface ContactErrors { name?: string; email?: string; subject?: string; message?: string; }

const ContactPage: React.FC = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [form, setForm] = useState<ContactForm>({ name: '', email: '', subject: '', message: '' });
  const [errors, setErrors] = useState<ContactErrors>({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const validate = (): boolean => {
    const e: ContactErrors = {};
    if (!form.name.trim()) e.name = language === 'en' ? 'Name is required' : 'Le nom est requis';
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = language === 'en' ? 'Valid email required' : 'Email valide requis';
    if (!form.subject.trim()) e.subject = language === 'en' ? 'Subject is required' : 'Le sujet est requis';
    if (!form.message.trim()) e.message = language === 'en' ? 'Message is required' : 'Le message est requis';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleChange = (field: keyof ContactForm) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    await new Promise(r => setTimeout(r, 1500));
    setLoading(false);
    setSuccess(true);
  };

  return (
    <Box>

      <PageHero
        title={language === 'en' ? 'Contact Us' : 'Nous Contacter'}
        subtitle={language === 'en'
          ? 'Find all the information you need to reach Hôpital Régional de Bafoussam'
          : 'Trouvez toutes les informations pour contacter l\'Hôpital Régional de Bafoussam'}
        breadcrumbs={[
          { label: language === 'en' ? 'Home' : 'Accueil', path: '/' },
          { label: language === 'en' ? 'Contact' : 'Contact' },
        ]}
        accentColor="#0B4F8A"
      />

      {/* MAIN CONTENT — narrow centered column */}
      <Box sx={{ bgcolor: 'white', py: { xs: 5, md: 8 } }}>
        <Container maxWidth="md">

          {/* Intro paragraphs */}
          <Typography variant="body1" color="text.secondary" lineHeight={1.9} mb={1.5}>
            {language === 'en'
              ? `Here you will find all information for contacting the Hôpital Régional de Bafoussam.`
              : `Ici vous trouverez toutes les informations pour contacter l'Hôpital Régional de Bafoussam.`}
          </Typography>
          <Typography variant="body1" color="text.secondary" lineHeight={1.9} mb={1.5}>
            {language === 'en'
              ? `You can come as a patient referred by the health insurance, the hospital fund, or as a private payer. Our hospital has a care contract with all statutory health insurances and is eligible for government subsidies.`
              : `Vous pouvez venir en tant que patient orienté par l'assurance maladie, la caisse hospitalière, ou en tant que payeur privé. Notre hôpital a un contrat de soins avec toutes les assurances maladie légales et est éligible aux subventions gouvernementales.`}
          </Typography>
          <Typography variant="body1" color="text.secondary" lineHeight={1.9} mb={1}>
            {language === 'en'
              ? 'Admission to our hospital can be done in the following ways:'
              : 'L\'admission dans notre hôpital peut se faire de plusieurs manières :'}
          </Typography>

          <Box component="ul" sx={{ pl: 3, mb: 3 }}>
            {(language === 'en' ? [
              'After a hospital stay by applying through the attending physician and social worker (post-treatment rehabilitation)',
              'By applying for authorization from the responsible cost bearer (e.g. health insurance, pension insurance, private insurance)',
              'By requesting and booking our programs as a private payer',
              'By phone or in person with a prescription for outpatient treatment',
            ] : [
              'Après une hospitalisation sur demande du médecin traitant et de l\'assistant social (rééducation post-traitement)',
              'En demandant une autorisation auprès de l\'organisme payeur compétent (ex : assurance maladie, retraite, assurance privée)',
              'En demandant et réservant nos programmes en tant que payeur privé',
              'Par téléphone ou en personne avec une ordonnance pour traitement ambulatoire',
            ]).map((item, i) => (
              <Typography component="li" key={i} variant="body2" color="text.secondary" lineHeight={1.9} mb={0.5}>
                {item}
              </Typography>
            ))}
          </Box>

          <Typography variant="body1" color="text.secondary" lineHeight={1.9} mb={0.5}>
            {language === 'en'
              ? 'Your registration at our hospital is handled by our patient services office at:'
              : 'Votre inscription dans notre hôpital est gérée par notre bureau des services aux patients au :'}
          </Typography>
          <Typography variant="body1" lineHeight={1.9} mb={0.5}>
            {language === 'en' ? 'Service Phone: ' : 'Téléphone service : '}
            <Typography component="span" fontWeight={700} color="primary.main">{emergencyNumbers.main}</Typography>
          </Typography>
          <Typography variant="body1" lineHeight={1.9} mb={0.5}>
            {language === 'en' ? 'or by email: ' : 'ou par e-mail : '}
            <Typography component="span" fontWeight={700} color="primary.main">{hospitalInfo.email}</Typography>
          </Typography>
          <Typography variant="body1" color="text.secondary" lineHeight={1.9} mb={4}>
            {language === 'en'
              ? 'For inquiries you can also use our form below. We look forward to your questions, wishes, or suggestions.'
              : 'Pour les demandes, vous pouvez également utiliser notre formulaire ci-dessous. Nous nous réjouissons de vos questions, souhaits ou suggestions.'}
          </Typography>

          {/* Card 1 — Patient Rights */}
          <Card
            elevation={1}
            sx={{
              mb: 3, borderLeft: '4px solid', borderColor: 'primary.main', borderRadius: 2,
            }}
          >
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 3, flexWrap: 'wrap' }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" fontWeight={700} color="primary.main" mb={0.5}>
                    {language === 'en' ? 'Patient Rights & Choice' : 'Droits & Choix du Patient'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {language === 'en' ? 'Choose your preferred hospital.' : 'Choisissez votre hôpital préféré.'}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  onClick={() => navigate('/patients')}
                  sx={{
                    bgcolor: '#0d1f3c', '&:hover': { bgcolor: '#0B4F8A' },
                    borderRadius: 6, px: 3, textTransform: 'uppercase',
                    fontWeight: 700, fontSize: '0.75rem', whiteSpace: 'nowrap',
                  }}
                >
                  {language === 'en' ? 'More Information' : 'Plus d\'Informations'}
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Card 2 — Patient Questionnaire */}
          <Card
            elevation={1}
            sx={{ mb: 6, borderLeft: '4px solid', borderColor: 'primary.main', borderRadius: 2 }}
          >
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 3, flexWrap: 'wrap' }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary" lineHeight={1.9} mb={1}>
                    {language === 'en'
                      ? 'In order to carry out your treatment quickly and in a targeted manner at the beginning of your stay, we need some information from you. We ask you to take the time to complete this admission form and submit it fully and completely before starting your treatment.'
                      : 'Afin de mener votre traitement rapidement et de manière ciblée dès le début de votre séjour, nous avons besoin de quelques informations de votre part. Nous vous demandons de prendre le temps de remplir ce formulaire d\'admission et de le soumettre complètement avant le début de votre traitement.'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" lineHeight={1.9}>
                    {language === 'en'
                      ? 'It is useful to bring your previous medical reports, medication lists, allergy pass, X-rays, blood pressure monitor, blood glucose meter, etc.'
                      : 'Il est utile d\'apporter vos rapports médicaux antérieurs, listes de médicaments, pass allergie, radiographies, tensiomètre, glycomètre, etc.'}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  onClick={() => navigate('/appointment')}
                  sx={{
                    bgcolor: '#0d1f3c', '&:hover': { bgcolor: '#0B4F8A' },
                    borderRadius: 6, px: 3, textTransform: 'uppercase',
                    fontWeight: 700, fontSize: '0.75rem', whiteSpace: 'nowrap',
                  }}
                >
                  {language === 'en' ? 'Patient Form' : 'Formulaire Patient'}
                </Button>
              </Box>
            </CardContent>
          </Card>

          <Divider sx={{ mb: 6 }} />

          {/* Contact info row */}
          <Grid container spacing={3} sx={{ mb: 6 }}>
            {[
              { icon: <LocationOn />, label: t.contact.address, value: language === 'en' ? hospitalInfo.addressEn : hospitalInfo.addressFr },
              { icon: <Phone />, label: t.contact.phone, value: `${emergencyNumbers.main}` },
              { icon: <Email />, label: t.contact.email, value: hospitalInfo.email },
              { icon: <AccessTime />, label: t.contact.hours, value: language === 'en' ? 'Mon–Fri: 7:00–18:00 | Emergency: 24/7' : 'Lun–Ven : 7h–18h | Urgences : 24h/24' },
            ].map((item, i) => (
              <Grid item xs={12} sm={6} key={i}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {item.icon}
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.7rem' }}>
                      {item.label}
                    </Typography>
                    <Typography variant="body2" lineHeight={1.6} mt={0.25}>{item.value}</Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>

          {/* Contact Form */}
          <Card elevation={1} sx={{ borderRadius: 2 }}>
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              <Typography variant="h5" fontWeight={700} mb={3}>{t.contact.sendMessage}</Typography>

              {success ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <CheckCircle sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
                  <Typography variant="h6" fontWeight={700} mb={1}>
                    {language === 'en' ? 'Message Sent!' : 'Message Envoyé !'}
                  </Typography>
                  <Typography color="text.secondary">{t.contact.successMsg}</Typography>
                  <Button variant="outlined" sx={{ mt: 3 }}
                    onClick={() => { setSuccess(false); setForm({ name: '', email: '', subject: '', message: '' }); }}>
                    {language === 'en' ? 'Send Another' : 'Envoyer un Autre'}
                  </Button>
                </Box>
              ) : (
                <Grid container spacing={2.5}>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label={t.contact.yourName} required
                      value={form.name} onChange={handleChange('name')}
                      error={!!errors.name} helperText={errors.name} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label={t.contact.yourEmail} type="email" required
                      value={form.email} onChange={handleChange('email')}
                      error={!!errors.email} helperText={errors.email} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth label={t.contact.subject} required
                      value={form.subject} onChange={handleChange('subject')}
                      error={!!errors.subject} helperText={errors.subject} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth label={t.contact.yourMessage} multiline rows={5} required
                      value={form.message} onChange={handleChange('message')}
                      error={!!errors.message} helperText={errors.message} />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      variant="contained" size="large" onClick={handleSubmit} disabled={loading}
                      startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <Send />}
                      sx={{ px: 4, borderRadius: 6 }}
                    >
                      {loading ? t.common.loading : t.contact.sendBtn}
                    </Button>
                  </Grid>
                </Grid>
              )}
            </CardContent>
          </Card>

          {/* Map placeholder */}
          <Card sx={{ overflow: 'hidden', mt: 3 }}>
            <Box sx={{ height: 220, bgcolor: '#E8F0FE', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
              {Array.from({ length: 8 }).map((_, i) => (
                <Box key={`h${i}`} sx={{ position: 'absolute', left: 0, right: 0, top: `${i * 14}%`, height: 1, bgcolor: '#C5D7FB' }} />
              ))}
              {Array.from({ length: 7 }).map((_, i) => (
                <Box key={`v${i}`} sx={{ position: 'absolute', top: 0, bottom: 0, left: `${i * 16}%`, width: 1, bgcolor: '#C5D7FB' }} />
              ))}
              <Box sx={{ position: 'relative', zIndex: 1, width: 44, height: 44, borderRadius: '50% 50% 50% 0', bgcolor: '#D32F2F', transform: 'rotate(-45deg)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(211,47,47,0.4)', mb: 1 }}>
                <LocalHospital sx={{ transform: 'rotate(45deg)', color: 'white', fontSize: 22 }} />
              </Box>
              <Typography variant="caption" fontWeight={700} sx={{ position: 'relative', zIndex: 1, color: '#1565C0', mt: 1 }}>
                Hôpital Régional de Bafoussam
              </Typography>
              <Typography variant="caption" sx={{ position: 'relative', zIndex: 1, color: '#555' }}>
                Avenue de l'Indépendance, Bafoussam
              </Typography>
            </Box>
            <Box sx={{ p: 2, textAlign: 'center', borderTop: '1px solid', borderColor: 'divider' }}>
              <Button variant="outlined" size="small" startIcon={<LocationOn />}>
                {language === 'en' ? 'Get Directions' : "Obtenir l'Itinéraire"}
              </Button>
            </Box>
          </Card>

        </Container>
      </Box>
    </Box>
  );
};

export default ContactPage;
